import type { ClientId, AreaId, WorkOrderId, ServiceFrontId, SignatoryId, ProfessionalId } from "../../../shared/kernel/identifiers.js";
import type {
  AdminRepository,
  Area,
  WorkOrder,
  SubWorkOrder,
  ServiceFront,
  Signatory,
  ProfessionalSubOsAccess,
  WorkOrderProjectLink
} from "../../../administration/admin-domain.js";

export class InMemoryAdminRepository implements AdminRepository {
  private areas = new Map<string, Area>();
  private workOrders = new Map<string, WorkOrder>();
  private subWorkOrders = new Map<string, SubWorkOrder>();
  private serviceFronts = new Map<string, ServiceFront>();
  private signatories = new Map<string, Signatory>();
  /**
   * Vínculo Sub-OS ↔ Colaborador. O contrato já previa a leitura, mas nenhum
   * repositório guardava o dado: sem isto, uma Sub-OS só podia ter o seu
   * responsável principal, e "um ou mais colaboradores" não existia de fato.
   */
  private professionalSubOsAccesses = new Map<string, ProfessionalSubOsAccess>();
  /** Vínculos OS ↔ Projeto: uma OS possui vários Projetos. */
  private workOrderProjectLinks = new Map<string, WorkOrderProjectLink>();

  constructor() {
    this.load();
    this.migrateLegacyWorkOrderProjectLinks();
  }

  /**
   * Migração dos vínculos antigos: cada OS gravada com o campo único
   *  ganha o vínculo equivalente na nova estrutura. Nada é
   * apagado e nada precisa ser recadastrado; rodar de novo não duplica.
   */
  private migrateLegacyWorkOrderProjectLinks(): void {
    let created = 0;

    for (const workOrder of this.workOrders.values()) {
      if (!workOrder.projectId) continue;

      const alreadyLinked = Array.from(this.workOrderProjectLinks.values()).some(
        link => link.workOrderId === workOrder.id && link.projectId === workOrder.projectId
      );
      if (alreadyLinked) continue;

      const id = `wopl-legacy-${workOrder.id}-${workOrder.projectId}`;
      this.workOrderProjectLinks.set(id, {
        id,
        clientId: workOrder.clientId,
        workOrderId: workOrder.id,
        projectId: workOrder.projectId,
        createdAt: workOrder.createdAt || new Date().toISOString(),
        createdBy: null
      });
      created++;
    }

    if (created > 0) this.persist();
  }

  private load(): void {
    try {
      const a = localStorage.getItem("inspect_areas");
      if (a) this.areas = new Map(JSON.parse(a));
      const wo = localStorage.getItem("inspect_work_orders");
      if (wo) this.workOrders = new Map(JSON.parse(wo));
      const swo = localStorage.getItem("inspect_sub_work_orders");
      if (swo) this.subWorkOrders = new Map(JSON.parse(swo));
      const sf = localStorage.getItem("inspect_service_fronts");
      if (sf) this.serviceFronts = new Map(JSON.parse(sf));
      const sig = localStorage.getItem("inspect_signatories");
      if (sig) this.signatories = new Map(JSON.parse(sig));
      const acc = localStorage.getItem("inspect_professional_sub_os_accesses");
      if (acc) this.professionalSubOsAccesses = new Map(JSON.parse(acc));
      const links = localStorage.getItem("inspect_work_order_project_links");
      if (links) this.workOrderProjectLinks = new Map(JSON.parse(links));
    } catch (e) {
      console.warn("Failed to load admin data from localStorage", e);
    }
  }

  private persist(): void {
    try {
      localStorage.setItem("inspect_areas", JSON.stringify(Array.from(this.areas.entries())));
      localStorage.setItem("inspect_work_orders", JSON.stringify(Array.from(this.workOrders.entries())));
      localStorage.setItem("inspect_sub_work_orders", JSON.stringify(Array.from(this.subWorkOrders.entries())));
      localStorage.setItem("inspect_service_fronts", JSON.stringify(Array.from(this.serviceFronts.entries())));
      localStorage.setItem("inspect_signatories", JSON.stringify(Array.from(this.signatories.entries())));
      localStorage.setItem("inspect_professional_sub_os_accesses", JSON.stringify(Array.from(this.professionalSubOsAccesses.entries())));
      localStorage.setItem("inspect_work_order_project_links", JSON.stringify(Array.from(this.workOrderProjectLinks.entries())));
    } catch (e) {
      console.warn("Failed to persist admin data to localStorage", e);
    }
  }

  // Areas
  async getAreas(clientId?: ClientId): Promise<Area[]> {
    if (!clientId) return Array.from(this.areas.values());
    return Array.from(this.areas.values()).filter(a => a.clientId === clientId);
  }

  async saveArea(area: Area): Promise<void> {
    this.areas.set(area.id, area);
    this.persist();
  }

  async inactivateArea(clientId: ClientId, id: AreaId): Promise<void> {
    const area = this.areas.get(id);
    if (area && (!clientId || area.clientId === clientId)) {
      this.areas.set(id, { ...area, status: 'INACTIVE', updatedAt: new Date().toISOString() });
      this.persist();
    }
  }

  async deleteArea(id: AreaId): Promise<void> {
    this.areas.delete(id);
    this.persist();
  }

  // Work Orders (OS)
  async getWorkOrders(clientId?: ClientId): Promise<WorkOrder[]> {
    if (!clientId) return Array.from(this.workOrders.values());
    return Array.from(this.workOrders.values()).filter(wo => wo.clientId === clientId);
  }

  async saveWorkOrder(workOrder: WorkOrder): Promise<void> {
    this.workOrders.set(workOrder.id, workOrder);
    this.persist();
  }

  async inactivateWorkOrder(clientId: ClientId, id: WorkOrderId): Promise<void> {
    const wo = this.workOrders.get(id);
    if (wo && (!clientId || wo.clientId === clientId)) {
      this.workOrders.set(id, { ...wo, status: 'CANCELLED', updatedAt: new Date().toISOString() });
      this.persist();
    }
  }

  async deleteWorkOrder(id: WorkOrderId): Promise<void> {
    if (this.workOrders.has(id)) {
      this.workOrders.delete(id);
      this.persist();
    }
  }

  // Sub-OS
  async getSubWorkOrders(clientId?: ClientId, workOrderId?: WorkOrderId): Promise<SubWorkOrder[]> {
    return Array.from(this.subWorkOrders.values()).filter(
      s => (!clientId || s.clientId === clientId) && (!workOrderId || s.workOrderId === workOrderId)
    );
  }

  async saveSubWorkOrder(subWorkOrder: SubWorkOrder): Promise<void> {
    this.subWorkOrders.set(subWorkOrder.id, subWorkOrder);
    this.persist();
  }

  async inactivateSubWorkOrder(clientId: ClientId, id: string): Promise<void> {
    const swo = this.subWorkOrders.get(id);
    if (swo && (!clientId || swo.clientId === clientId)) {
      this.subWorkOrders.set(id, { ...swo, status: 'CANCELLED', updatedAt: new Date().toISOString() });
      this.persist();
    }
  }

  async deleteSubWorkOrder(id: string): Promise<void> {
    if (this.subWorkOrders.has(id)) {
      this.subWorkOrders.delete(id);
      this.persist();
    }
  }

  // Service Fronts
  async getServiceFronts(clientId?: ClientId, workOrderId?: WorkOrderId): Promise<ServiceFront[]> {
    return Array.from(this.serviceFronts.values()).filter(
      sf => (!clientId || sf.clientId === clientId) && (!workOrderId || sf.workOrderId === workOrderId)
    );
  }

  async saveServiceFront(serviceFront: ServiceFront): Promise<void> {
    this.serviceFronts.set(serviceFront.id, serviceFront);
    this.persist();
  }

  async inactivateServiceFront(clientId: ClientId, id: ServiceFrontId): Promise<void> {
    const sf = this.serviceFronts.get(id);
    if (sf && sf.clientId === clientId) {
      this.serviceFronts.set(id, { ...sf, status: 'INACTIVE', updatedAt: new Date().toISOString() });
      this.persist();
    }
  }

  async deleteServiceFront(clientId: ClientId, id: ServiceFrontId): Promise<void> {
    const sf = this.serviceFronts.get(id);
    if (sf && sf.clientId === clientId) {
      this.serviceFronts.delete(id);
      this.persist();
    }
  }

  // Signatories
  async getSignatories(clientId: ClientId): Promise<Signatory[]> {
    return Array.from(this.signatories.values()).filter(s => s.clientId === clientId);
  }

  async getSignatoryById(id: SignatoryId): Promise<Signatory | null> {
    return this.signatories.get(id) ?? null;
  }

  async saveSignatory(signatory: Signatory): Promise<void> {
    this.signatories.set(signatory.id, signatory);
    this.persist();
  }

  async inactivateSignatory(clientId: ClientId, id: SignatoryId): Promise<void> {
    const s = this.signatories.get(id);
    if (s && s.clientId === clientId) {
      this.signatories.set(id, { ...s, status: 'INACTIVE', updatedAt: new Date().toISOString() });
      this.persist();
    }
  }

  async deleteSignatory(id: SignatoryId): Promise<void> {
    if (this.signatories.has(id)) {
      this.signatories.delete(id);
      this.persist();
    }
  }

  // OS ↔ Projeto
  async getWorkOrderProjectLinks(clientId?: ClientId, filters?: { workOrderId?: WorkOrderId; projectId?: string }): Promise<WorkOrderProjectLink[]> {
    return Array.from(this.workOrderProjectLinks.values()).filter(
      link =>
        (!clientId || link.clientId === clientId) &&
        (!filters?.workOrderId || link.workOrderId === filters.workOrderId) &&
        (!filters?.projectId || link.projectId === filters.projectId)
    );
  }

  async saveWorkOrderProjectLink(link: WorkOrderProjectLink): Promise<void> {
    this.workOrderProjectLinks.set(link.id, link);
    this.persist();
  }

  async deleteWorkOrderProjectLink(id: string): Promise<void> {
    if (this.workOrderProjectLinks.has(id)) {
      this.workOrderProjectLinks.delete(id);
      this.persist();
    }
  }

  // Sub-OS ↔ Colaborador
  async getProfessionalSubOsAccesses(clientId?: ClientId, professionalId?: ProfessionalId): Promise<ProfessionalSubOsAccess[]> {
    return Array.from(this.professionalSubOsAccesses.values()).filter(
      access => (!clientId || access.clientId === clientId) && (!professionalId || access.professionalId === professionalId)
    );
  }

  async saveProfessionalSubOsAccess(access: ProfessionalSubOsAccess): Promise<void> {
    this.professionalSubOsAccesses.set(access.id, access);
    this.persist();
  }

  async deleteProfessionalSubOsAccess(id: string): Promise<void> {
    if (this.professionalSubOsAccesses.has(id)) {
      this.professionalSubOsAccesses.delete(id);
      this.persist();
    }
  }
}
